import { exactCount } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Access & Audit › GDPR requests (`ac-gdpr`), read from
 * `GET /v1/admin/privacy/requests` (go-wallet#523, handler
 * `internal/server/admin_privacy.go`).
 *
 * # There is no fulfil control, and that is a decision rather than a gap
 *
 * The endpoint says so in its own words, and it is the reason this screen is a
 * read:
 *
 *   > READS ONLY, permanently. There is no fulfil control here and there must
 *   > not be one: honouring an erasure is irreversible and is CORRECTLY REFUSED
 *   > most of the time, because the AMLD5 Art. 40 retention clock outlives the
 *   > GDPR deadline for the majority of these requests. An irreversible action
 *   > that is usually refused is not a button (#363) — a console dialog is a
 *   > thing somebody clicks past, while a CLI refusal naming the obligation is
 *   > a thing they read.
 *
 * So this console will never grow an "erase" button here, and the screen states
 * that rather than leaving its absence to be read as unfinished work. Erasures
 * run through `wallet-admin privacy fulfil`.
 *
 * # What the screen is FOR is the deadline
 *
 * Art. 12(3) gives a statutory month, implemented as 30 days because "one month
 * from 31 January" has no agreed answer. Until this read, which requests were
 * open and how long they had left was answerable only from a shell — survivable
 * at zero volume and not at any real one, for a deadline that is statutory.
 *
 * Rows arrive ordered by deadline, soonest first, and are NOT re-sorted here:
 * the first row is the one that will breach next, and that ordering is the
 * backend's statement rather than this console's arithmetic.
 *
 * # Late and remaining are two fields, never one signed number
 *
 * `overdue_seconds` and `remaining_seconds` are separate on the wire, and the
 * reason is on the tin: "eleven days late" and "eleven days left" must not be
 * renderable from the same value with a wrong sign. They stay separate here.
 *
 * # `truncated` is not decoration
 *
 * The read is bounded, because an unbounded one times out on the day the
 * backlog is worst. A bounded page that does not say so lets an operator work
 * 500 rows believing they are all of them, against a promise that every open
 * request is visible. So `truncated` is rendered, loudly, and `open` is
 * compared against the rows that arrived.
 *
 * # How we know it is them
 *
 * `verification` separates a request made from an authenticated session from
 * one that arrived by post and was checked by a human. They are different
 * evidential animals, and collapsing them is how the second quietly starts
 * counting as the first.
 */

export interface PrivacyRequestView {
  readonly reference: string
  readonly customer_id: number
  readonly state: string
  readonly channel: string
  readonly verification: string
  readonly submitted_at: string
  readonly due_at: string
  readonly overdue: boolean
  readonly overdue_seconds?: number
  readonly remaining_seconds?: number
}

export interface PrivacyResponse {
  readonly requests: readonly unknown[]
  readonly overdue?: unknown
  readonly open?: unknown
  readonly truncated?: unknown
  readonly sla_days?: unknown
}

export const PRIVACY_PATH = '/privacy/requests'

const SOURCE = 'postgres · privacy_requests (GET /v1/admin/privacy/requests)'
const AUTHORITY = 'operational · deadlines computed against one instant'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactCount(value)
}

interface Request {
  readonly reference: string
  readonly customerId: number | null
  readonly state: string
  readonly channel: string
  readonly verification: string
  readonly submittedAt: string
  readonly dueAt: string
  /** Three-state. An absent flag is not "on time" — see `overdueChip`. */
  readonly overdue: boolean | null
  readonly overdueSeconds: number | null
  readonly remainingSeconds: number | null
}

function readRequest(raw: unknown): Request | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const r = raw as Partial<PrivacyRequestView>
  const reference = text(r.reference)
  // The reference is the token joining the request, the decision and the
  // redaction, and it is constrained to a reference shape precisely so the
  // receipt for erasing a person can never quote the person. A row without one
  // cannot be acted on or joined to anything.
  if (reference === '') return null
  return {
    reference,
    customerId: whole(r.customer_id),
    state: text(r.state),
    channel: text(r.channel),
    verification: text(r.verification),
    submittedAt: text(r.submitted_at),
    dueAt: text(r.due_at),
    overdue: typeof r.overdue === 'boolean' ? r.overdue : null,
    overdueSeconds: whole(r.overdue_seconds),
    remainingSeconds: whole(r.remaining_seconds),
  }
}

/** Seconds as whole days and hours. Never rounded up into "still has time". */
function span(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days === 0) return `${String(hours)}h`
  return `${String(days)}d ${String(hours)}h`
}

/**
 * The deadline cell: late by, or left.
 *
 * Two separate fields on the wire and two separate readings here. A row flagged
 * overdue that carries no `overdue_seconds` says it is late without saying how
 * late — which is honest, and better than borrowing `remaining_seconds` and
 * printing a duration with the wrong sign.
 */
function deadlineCell(r: Request): ChipCell {
  if (r.overdue === null) return ['— not stated', 'r']
  if (r.overdue) {
    return [r.overdueSeconds === null ? 'LATE' : `LATE by ${span(r.overdueSeconds)}`, 'r']
  }
  if (r.remainingSeconds === null) return ['within the deadline', 'g']
  // Under two days is amber: a statutory deadline with 36 hours left is not the
  // same posture as one with three weeks, and the row should not read alike.
  const tone = r.remainingSeconds < 172800 ? 'a' : 'g'
  return [`${span(r.remainingSeconds)} left`, tone]
}

/**
 * How the request was verified.
 *
 * `operator_verified` carries its own tone, not because it is worse, but
 * because it is the one that rests on a human's judgement and is the one an
 * auditor will ask about.
 */
function verificationCell(r: Request): ChipCell {
  if (r.verification === 'authenticated_session') return ['authenticated session', 'n']
  if (r.verification === 'operator_verified') return ['operator verified', 'a']
  return [r.verification === '' ? '— not stated' : `unknown: ${r.verification}`, 'r']
}

const COLS: readonly Column[] = [
  ['Reference', 'm', 'minmax(150px,1.1fr)'],
  ['Customer', 'm', '110px'],
  ['State', 'x', 'minmax(150px,1.1fr)'],
  ['Channel', 'x', '130px'],
  ['How verified', 'c', '170px'],
  ['Submitted', 'm', '120px'],
  ['Due', 'm', '120px'],
  ['Deadline', 'c', '170px'],
]

function row(r: Request): readonly Cell[] {
  return [
    r.reference,
    r.customerId === null ? '— not returned' : String(r.customerId),
    r.state === '' ? '— not stated' : r.state,
    r.channel === '' ? '— not stated' : r.channel,
    verificationCell(r),
    r.submittedAt === '' ? '— not returned' : r.submittedAt.slice(0, 10),
    r.dueAt === '' ? '— not returned' : r.dueAt.slice(0, 10),
    deadlineCell(r),
  ]
}

function queueTable(
  rows: readonly Request[],
  open: number | null,
  truncated: boolean | null,
  unreadable: number,
  slaDays: number | null,
): TableBlock {
  const shown = String(rows.length)
  const base =
    open === null
      ? `${shown} request(s) on this page`
      : truncated === true
        ? `${shown} shown of ${String(open)} open — THIS IS A PAGE, not the queue`
        : `${shown} shown of ${String(open)} open`
  // The window "overdue" is measured against, from the backend rather than
  // from the Art. 12(3) constant in this file's header. A console that hard-
  // coded 30 days would keep saying 30 on a deployment that had changed it.
  const head = slaDays === null ? base : `${base} · deadline ${String(slaDays)} days`
  return {
    t: 'table',
    title: 'privacy_requests · open, soonest deadline first',
    src: 'ledger',
    facets: false,
    cols: COLS,
    // Not re-sorted. The order is the backend's statement that this is the row
    // which breaches next; re-deriving it here would be a second implementation
    // of a statutory deadline.
    rows: rows.map(row),
    footer: unreadable === 0 ? head : `${head} · ${String(unreadable)} row(s) could not be read`,
    foot2:
      'Late and remaining are two separate figures on the wire and stay separate here, so "eleven days late" and "eleven days left" can never be rendered from one value with a wrong sign.',
  }
}

const NO_FULFIL: Block = {
  t: 'note',
  tone: 'a',
  kicker: 'By design',
  title: 'There is no erase control on this screen, and there will not be one',
  body: 'Honouring an erasure is irreversible, and it is correctly refused most of the time: the AMLD5 Art. 40 retention clock outlives the GDPR deadline for the majority of these requests. An irreversible action that is usually refused is not a button — a dialog is a thing somebody clicks past, while a command-line refusal naming the obligation is a thing they read. Erasures are run with `wallet-admin privacy fulfil`.',
  ref: 'go-wallet#363 · this endpoint is read-only, permanently',
}

const RETENTION: Block = {
  t: 'note',
  tone: 'a',
  kicker: 'Retention',
  title: 'Deletion is logical, and the financial record is kept regardless',
  body: 'Postings, legs, statements, fee events and provider events are retained for the statutory period through the institution’s obligations, whatever a data subject asks for. No path in this console deletes a ledger row, and none will: the erasure that does run redacts product data and leaves the financial record standing.',
  ref: 'law 5 · append-only data has no delete affordance anywhere in this console',
}

function truncatedNote(open: number | null, shown: number): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Partial queue',
    title: 'This is a page, not the queue',
    body: `The read is bounded so it cannot time out on the day the backlog is worst${open === null ? '' : `, and ${String(open)} request(s) are open against ${String(shown)} shown`}. Working this page as though it were the whole queue is exactly the mistake the flag exists to prevent — every open request is supposed to be visible, and here it is not.`,
    ref: 'the deadline on a request you cannot see is running anyway',
  }
}

function emptyNote(): Block {
  return {
    t: 'note',
    tone: 'g',
    kicker: 'GDPR requests',
    title: 'No request is open',
    body: 'Requests arrive from the app and from support. An empty queue means none is outstanding — not that the intake is broken, and not that none has ever been made: this read covers what is open.',
    ref: 'privacy_requests · the read returned no open rows',
  }
}

function unreadableRowsNote(count: number): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Partial read',
    title: `${String(count)} request(s) could not be read`,
    body: 'Counted rather than dropped. A statutory deadline runs whether or not its row rendered, and a queue quietly shorter than the record is how one is missed.',
    ref: 'a request without a reference cannot be joined to its decision or its redaction and is refused',
  }
}

function contradiction(rows: readonly Request[], overdue: number | null, truncated: boolean | null): Block | null {
  const lateHere = rows.filter((r) => r.overdue === true).length
  // A page that says so cannot contradict a whole-queue count: fewer late rows
  // than the total is exactly what truncation means.
  if (overdue === null || truncated === true || overdue >= lateHere) return null
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Disagreement',
    title: 'More rows here are late than the queue reports',
    body: 'Both figures came from one read and neither is stated here as the answer. The rows are what an operator can act on, so they are what this screen shows — but a whole-queue count lower than the page beneath it is worth raising rather than smoothing over.',
    ref: `reported ${String(overdue)} late · ${String(lateHere)} late on this page`,
  }
}

export function composePrivacyRequests(res: PrivacyResponse, readAt: Date): ScreenData {
  const rows: Request[] = []
  let unreadable = 0
  for (const raw of res.requests) {
    const parsed = readRequest(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const overdue = whole(res.overdue)
  const open = whole(res.open)
  const truncated = typeof res.truncated === 'boolean' ? res.truncated : null

  const slaDays = whole(res.sla_days)
  const blocks: Block[] = [queueTable(rows, open, truncated, unreadable, slaDays)]
  if (truncated === true) blocks.push(truncatedNote(open, rows.length))
  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(emptyNote())
  const clash = contradiction(rows, overdue, truncated)
  if (clash !== null) blocks.push(clash)
  blocks.push(NO_FULFIL, RETENTION)

  const missing: string[] = []
  if (overdue === null) missing.push('how many open requests are past their deadline')
  if (open === null) missing.push('how many requests are open in total')
  if (truncated === null) missing.push('whether this is the whole queue or a page of it')

  const provenance: DataSource =
    missing.length === 0
      ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
      : {
          kind: 'partial',
          source: SOURCE,
          authority: AUTHORITY,
          readAt: readAtStamp(readAt),
          missing,
        }

  return { blocks, provenance, rowKeys: rows.map((r) => r.reference) }
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'GDPR requests',
      title: 'The request queue could not be read',
      body: `${reason} No request, no deadline and no breach below is known — and an unread queue is not an empty one. The statutory clock on every open request is running whether or not this screen can see it.`,
      ref: decisionRef === null
        ? 'No request was read. This is not an empty queue.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function isPrivacyResponse(payload: unknown): payload is PrivacyResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<PrivacyResponse>).requests)
}

export const loadPrivacyRequests: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(PRIVACY_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The privacy-requests endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isPrivacyResponse(payload)) {
    return unreadableScreen(
      'The privacy-requests endpoint returned a shape this console does not know.',
      null,
    )
  }
  return composePrivacyRequests(payload, new Date())
}
